import { differenceInCalendarDays } from 'date-fns';
import { createDateSafe } from '@/utils/dateUtils';
import type { VacationDpRow } from '../models/vacationDpBoard.types';
import { sortSegmentsByDate } from './vacationSegments';

export type DpDeadlineTone = 'danger' | 'warning' | 'none' | 'muted';

export interface DpDeadlineInfo {
    /** 'approve', 'expired', 'expiring', 'scheduled', 'acquisition', 'ineligible', 'onVacation' */
    kind: string;
    /** Texto principal a exibir (ex: "decidir até 12/10", "vencido") */
    label: string;
    /** Texto secundário (ex: "faltam 6 dias", "risco de pagar em dobro") */
    hint: string | null;
    /** Cor: danger | warning | none | muted */
    tone: DpDeadlineTone;
}

/**
 * Resolve o prazo que importa para cada linha, eliminando a ambiguidade das duas colunas antigas.
 *
 * Retorna um objeto com a informação de prazo **contextual** — o que o DP precisa saber naquele
 * estado específico do colaborador. Reusa a lógica de faixas (0/7/15 dias) que hoje é duplicada
 * entre a tabela e o card mobile.
 *
 * Estados possíveis (na ordem de precedência):
 * 1. **Inelegível** — não tem direito (PJ, estagiário, inativo)
 * 2. **Férias agendadas** — períodos aprovados (começadas ou não)
 * 3. **Solicitação aguardando decisão** — prazo para o DP decidir (início − 30 dias)
 * 4. **Vencido sem solicitação** — dias do art. 137 em risco de dobra
 * 5. **Prazo a vencer (sem solicitação)** — próximas 30 dias até o limite de agendamento
 * 6. **Prazo projetado (sem solicitação)** — ainda em aquisição, sem solicitação aberta
 */
export function resolveDpDeadline(row: VacationDpRow, today: Date = new Date()): DpDeadlineInfo {
    // 1. Ineligível
    if (!row.eligible) {
        return {
            kind: 'ineligible',
            label: 'não se aplica',
            hint: null,
            tone: 'muted',
        };
    }

    // 2. Férias já agendadas (aprovadas) — conferir ANTES do prazo de vencimento
    if (row.approvedPeriods && row.approvedPeriods.length > 0) {
        const nextPeriod = row.approvedPeriods.find((p) => p.status !== 'Completed');
        if (nextPeriod) {
            const start = createDateSafe(nextPeriod.startDate);
            const end = createDateSafe(nextPeriod.endDate);

            if (start && end) {
                const daysUntilStart = differenceInCalendarDays(start, today);

                if (daysUntilStart < 0) {
                    // Já começou — de férias agora
                    return {
                        kind: 'onVacation',
                        label: `de férias: ${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')}`,
                        hint: null,
                        tone: 'none',
                    };
                }

                // Ainda não começou — agendadas para depois
                return {
                    kind: 'scheduled',
                    label: `agendada: ${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')}`,
                    hint: `inicia em ${daysUntilStart} ${daysUntilStart === 1 ? 'dia' : 'dias'}`,
                    tone: 'none',
                };
            }
        }
    }

    // 3. Solicitação aguardando aprovação do DP
    if (row.request && row.request.status === 'PendingDP' && row.approvalDeadline) {
        const deadline = createDateSafe(row.approvalDeadline);
        if (deadline) {
            const days = differenceInCalendarDays(deadline, today);
            let tone: DpDeadlineTone = 'none';
            if (days <= 7) tone = 'danger';
            else if (days <= 15) tone = 'warning';

            const label =
                days < 0
                    ? `vencido há ${Math.abs(days)} ${days === -1 ? 'dia' : 'dias'}`
                    : `decidir até ${deadline.toLocaleDateString('pt-BR')}`;
            const hint =
                days < 0
                    ? 'prazo de aprovação passou'
                    : `faltam ${days} ${days === 1 ? 'dia' : 'dias'}`;

            return {
                kind: 'approve',
                label,
                hint,
                tone,
            };
        }
    }

    // 4. Vencido sem solicitação aberta
    if (row.expiredDays > 0) {
        return {
            kind: 'expired',
            label: `${row.expiredDays} dia${row.expiredDays === 1 ? '' : 's'} vencido${row.expiredDays === 1 ? '' : 's'}`,
            hint: 'risco de pagar em dobro (art. 137)',
            tone: 'danger',
        };
    }

    // 5. Prazo a vencer
    if (row.concessiveDeadline && !row.deadlineIsProjected) {
        const deadline = createDateSafe(row.concessiveDeadline);
        if (deadline) {
            const days = differenceInCalendarDays(deadline, today);

            let tone: DpDeadlineTone = 'none';
            if (days <= 0) tone = 'danger';
            else if (days <= 30) tone = 'warning';

            const label =
                days <= 0
                    ? `vencido há ${Math.abs(days)} ${days === 0 ? 'dia' : 'dias'}`
                    : `vence em ${days} ${days === 1 ? 'dia' : 'dias'}`;

            return {
                kind: 'expiring',
                label,
                hint: null,
                tone,
            };
        }
    }

    // 6. Prazo projetado (ainda em aquisição)
    if (row.deadlineIsProjected && row.concessiveDeadline) {
        return {
            kind: 'acquisition',
            label: 'em aquisição',
            hint: null,
            tone: 'muted',
        };
    }

    // Fallback: sem prazo definido (ainda na aquisição inicial, muito cedo)
    return {
        kind: 'acquisition',
        label: 'em aquisição',
        hint: null,
        tone: 'muted',
    };
}

export interface DpRequestPeriodInfo {
    label: string;
    hint: string | null;
    tone: DpDeadlineTone;
}

export function resolveRequestPeriod(row: VacationDpRow, today: Date = new Date()): DpRequestPeriodInfo {
    if (!row.request) {
        return {
            label: '–',
            hint: null,
            tone: 'muted',
        };
    }

    if (row.approvedPeriods && row.approvedPeriods.length > 0) {
        const nextPeriod = row.approvedPeriods.find((p) => p.status !== 'Completed');
        if (nextPeriod) {
            const start = createDateSafe(nextPeriod.startDate);
            const end = createDateSafe(nextPeriod.endDate);

            if (start && end) {
                const daysUntilStart = differenceInCalendarDays(start, today);

                if (daysUntilStart < 0) {
                    return {
                        label: `de férias: ${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')}`,
                        hint: null,
                        tone: 'none',
                    };
                }

                return {
                    label: `agendada: ${start.toLocaleDateString('pt-BR')} a ${end.toLocaleDateString('pt-BR')}`,
                    hint: `inicia em ${daysUntilStart} ${daysUntilStart === 1 ? 'dia' : 'dias'}`,
                    tone: 'none',
                };
            }
        }
    }

    if (row.request.segments && row.request.segments.length > 0) {
        const sorted = sortSegmentsByDate(row.request.segments);
        const startDate = createDateSafe(sorted[0].startDate);
        const endDate = createDateSafe(sorted[sorted.length - 1].endDate);

        if (startDate && endDate) {
            const label = `${startDate.toLocaleDateString('pt-BR')} a ${endDate.toLocaleDateString('pt-BR')}`;
            const hint = row.approvalDeadline ? `decidir até ${createDateSafe(row.approvalDeadline)?.toLocaleDateString('pt-BR')}` : null;

            return {
                label,
                hint,
                tone: 'none',
            };
        }
    }

    return {
        label: '–',
        hint: null,
        tone: 'muted',
    };
}

export interface DpExpiryInfo {
    label: string;
    hint: string | null;
    tone: DpDeadlineTone;
}

export function resolveExpiryInfo(row: VacationDpRow, today: Date = new Date()): DpExpiryInfo {
    if (!row.eligible) {
        return {
            label: 'não se aplica',
            hint: null,
            tone: 'muted',
        };
    }

    if (row.expiredDays > 0) {
        return {
            label: `${row.expiredDays} dia${row.expiredDays === 1 ? '' : 's'} vencido${row.expiredDays === 1 ? '' : 's'}`,
            hint: 'risco de pagar em dobro (art. 137)',
            tone: 'danger',
        };
    }

    if (row.concessiveDeadline && !row.deadlineIsProjected) {
        const deadline = createDateSafe(row.concessiveDeadline);
        if (deadline) {
            const days = differenceInCalendarDays(deadline, today);

            let tone: DpDeadlineTone = 'none';
            if (days <= 0) tone = 'danger';
            else if (days <= 30) tone = 'warning';

            const label =
                days <= 0
                    ? `vencido há ${Math.abs(days)} ${days === 0 ? 'dia' : 'dias'}`
                    : `vence em ${days} ${days === 1 ? 'dia' : 'dias'}`;

            return {
                label,
                hint: null,
                tone,
            };
        }
    }

    if (row.deadlineIsProjected && row.concessiveDeadline) {
        return {
            label: 'em aquisição',
            hint: null,
            tone: 'muted',
        };
    }

    return {
        label: 'em aquisição',
        hint: null,
        tone: 'muted',
    };
}

/**
 * Extrai uma "dica" de uma linha para renderização resumida na célula de saldo.
 * Retorna **uma** sublinha mais relevante, para manter a densidade sob controle.
 */
export function resolveBalanceHint(row: VacationDpRow): string | null {
    // Prioridade: síntese de qualidade de dado > agendado > aguardando > histórico
    if (row.unallocatedDays > 0) {
        return `⚠ histórico maior que direito`;
    }
    if (row.scheduledDays > 0) {
        return `${row.scheduledDays}d agendados`;
    }
    if (row.pendingDays > 0) {
        return `${row.pendingDays}d aguardando aprovação`;
    }
    if (row.legacyTakenDays > 0) {
        return `${row.legacyTakenDays}d do histórico`;
    }
    return null;
}
